/**
 * News API client for news articles and sentiment data
 *
 * Fetches data from Phase 9 (News & Sentiment) endpoints
 */
import { apiClient } from './client'
import type {
  NewsListResponse,
  NewsSummaryResponse,
  MaterialNewsResponse,
  NewsFeedResponse,
  NewsArticleWithSentiment,
  SymbolsWithNewsResponse,
} from '../types/news'

// =============================================================================
// Parameter Interfaces
// =============================================================================

export interface FetchNewsForSymbolParams {
  days?: number // Lookback period in days (default 30)
  limit?: number
  offset?: number
  category?: 'company' | 'general'
  sentiment?: 'positive' | 'negative' | 'neutral'
  materialOnly?: boolean
}

export interface FetchNewsFeedParams {
  symbols?: string[] // Symbols to filter
  categories?: string[] // Categories to filter (company, general)
  days?: number // Lookback period in days (default 7)
  limit?: number
  offset?: number
  sentiment?: 'positive' | 'negative' | 'neutral'
  materialOnly?: boolean
}

export interface FetchMaterialNewsParams {
  symbols?: string[]
  days?: number
  limit?: number
}

// =============================================================================
// News by Symbol API
// =============================================================================

/**
 * Fetch news articles for a specific symbol
 * @param symbol Stock ticker symbol
 * @param params Optional filters (days, limit, offset, category, sentiment, materialOnly)
 */
export async function fetchNewsForSymbol(
  symbol: string,
  params?: FetchNewsForSymbolParams
): Promise<NewsListResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.days !== undefined) {
    queryParams.days = params.days.toString()
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit.toString()
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset.toString()
  }
  if (params?.category) {
    queryParams.category = params.category
  }
  if (params?.sentiment) {
    queryParams.sentiment = params.sentiment
  }
  if (params?.materialOnly) {
    queryParams.material_only = 'true'
  }

  const response = await apiClient.get<NewsListResponse>(
    `/api/news/${encodeURIComponent(symbol)}`,
    { params: queryParams }
  )
  return response.data
}

/**
 * Fetch news summary statistics for a symbol
 * @param symbol Stock ticker symbol
 * @param days Lookback period in days (default 30)
 */
export async function fetchNewsSummary(
  symbol: string,
  days?: number
): Promise<NewsSummaryResponse> {
  const queryParams: Record<string, string> = {}

  if (days !== undefined) {
    queryParams.days = days.toString()
  }

  const response = await apiClient.get<NewsSummaryResponse>(
    `/api/news/${encodeURIComponent(symbol)}/summary`,
    { params: queryParams }
  )
  return response.data
}

// =============================================================================
// Material News API
// =============================================================================

/**
 * Fetch material news across multiple symbols
 * @param params Optional filters (symbols, days, limit)
 */
export async function fetchMaterialNews(
  params?: FetchMaterialNewsParams
): Promise<MaterialNewsResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.symbols?.length) {
    queryParams.symbols = params.symbols.join(',')
  }
  if (params?.days !== undefined) {
    queryParams.days = params.days.toString()
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit.toString()
  }

  const response = await apiClient.get<MaterialNewsResponse>(
    '/api/news/material',
    { params: queryParams }
  )
  return response.data
}

// =============================================================================
// News Feed API
// =============================================================================

/**
 * Fetch combined news feed for News page
 * @param params Optional filters (symbols, categories, days, limit, offset, sentiment, materialOnly)
 */
export async function fetchNewsFeed(
  params?: FetchNewsFeedParams
): Promise<NewsFeedResponse> {
  const queryParams: Record<string, string> = {}

  if (params?.symbols?.length) {
    queryParams.symbols = params.symbols.join(',')
  }
  if (params?.categories?.length) {
    queryParams.categories = params.categories.join(',')
  }
  if (params?.days !== undefined) {
    queryParams.days = params.days.toString()
  }
  if (params?.limit !== undefined) {
    queryParams.limit = params.limit.toString()
  }
  if (params?.offset !== undefined) {
    queryParams.offset = params.offset.toString()
  }
  if (params?.sentiment) {
    queryParams.sentiment = params.sentiment
  }
  if (params?.materialOnly) {
    queryParams.material_only = 'true'
  }

  const response = await apiClient.get<NewsFeedResponse>(
    '/api/news/feed',
    { params: queryParams }
  )
  return response.data
}

// =============================================================================
// Article Detail API
// =============================================================================

/**
 * Fetch detailed news article by ID
 * @param articleId Database ID of the article
 */
export async function fetchNewsArticle(
  articleId: number
): Promise<NewsArticleWithSentiment> {
  const response = await apiClient.get<NewsArticleWithSentiment>(
    `/api/news/article/${articleId}`
  )
  return response.data
}

// =============================================================================
// Symbol List API
// =============================================================================

/**
 * Fetch list of symbols with available news data
 */
export async function fetchSymbolsWithNews(): Promise<SymbolsWithNewsResponse> {
  const response = await apiClient.get<SymbolsWithNewsResponse>(
    '/api/news/symbols'
  )
  return response.data
}

// =============================================================================
// Aggregated News API Object
// =============================================================================

export const newsApi = {
  // Symbol-specific
  getNewsForSymbol: fetchNewsForSymbol,
  getNewsSummary: fetchNewsSummary,

  // Material news
  getMaterialNews: fetchMaterialNews,

  // News feed
  getNewsFeed: fetchNewsFeed,

  // Article detail
  getArticle: fetchNewsArticle,

  // Symbol list
  getSymbolsWithNews: fetchSymbolsWithNews,
}
